import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import TrustMarquee from "../components/TrustMarquee";
import ApplyButton from "../components/ApplyButton";
import { Reveal } from "../components/Reveal";
import { categoryOf } from "../lib/category";

const CARRIER_OPTIONS = [
  { key: "all", label: "All", kw: [] },
  { key: "unlocked", label: "Unlocked", kw: ["unlock"] },
  { key: "tmobile", label: "T-Mobile/Metro", kw: ["t-mobile", "tmobile", "metro"] },
  { key: "verizon", label: "Verizon", kw: ["verizon"] },
  { key: "att", label: "AT&T/Cricket", kw: ["at&t", "att", "cricket"] },
];

const CONNECTIVITY_OPTIONS = [
  { key: "all", label: "All", kw: [] },
  { key: "wifi", label: "WiFi", kw: ["wifi", "wi-fi"] },
  { key: "cellular", label: "Cellular Unlocked", kw: ["cellular"] },
];

const CATEGORY_CONFIG = {
  iphone: {
    heading: "Certified iPhones, ready to lease or buy",
    noun: "iPhone",
    placeholder: "e.g. iPhone 15 Pro",
    testid: "products-page",
    filterKey: "carrier",
    filterLabel: "Carrier Selection",
    filterOptions: CARRIER_OPTIONS,
  },
  ipad: {
    heading: "Certified iPads, ready to lease or buy",
    noun: "iPad",
    placeholder: "e.g. iPad Pro",
    testid: "ipads-page",
    filterKey: "connectivity",
    filterLabel: "Connectivity Selection",
    filterOptions: CONNECTIVITY_OPTIONS,
  },
  macbook: {
    heading: "Certified MacBooks, ready to lease or buy",
    noun: "MacBook",
    placeholder: "e.g. MacBook Air",
    testid: "macbooks-page",
  },
};

// Release year parsed from the product name (iPads include it, e.g. "iPad Air M3 - 11-Inch - 2025 - WiFi").
const getYear = (p) => {
  const m = (p.name || "").match(/\b(20\d{2})\b/);
  return m ? m[1] : null;
};

// Match a product against the selected carrier/connectivity option.
const matchFilterOption = (opt, p, filterKey) => {
  if (!opt || opt.key === "all") return true;
  if (filterKey === "connectivity") {
    const hay = `${p.name || ""} ${p.carrier || ""}`.toLowerCase();
    if (opt.key === "wifi") return hay.includes("wifi") && !hay.includes("cellular");
    return opt.kw.some((k) => hay.includes(k));
  }
  const carrierStr = (p.carrier || "").toLowerCase();
  return opt.kw.some((k) => carrierStr.includes(k));
};

export default function Products({ category = "iphone" }) {
  const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.iphone;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [storage, setStorage] = useState("all");
  const [carrier, setCarrier] = useState("all");
  const [maxMonthly, setMaxMonthly] = useState(100);
  const [sort, setSort] = useState("featured");
  const [year, setYear] = useState("all");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Only products for this tab (iphone / ipad / macbook), by title keyword.
  const categoryProducts = useMemo(
    () => products.filter((p) => categoryOf(p) === category),
    [products, category]
  );

  const storages = useMemo(() => {
    const set = new Set();
    categoryProducts.forEach((p) =>
      (p.storage || "")
        .split(/,\s*/)
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((s) => set.add(s))
    );
    const toGB = (s) => {
      const n = parseFloat(s) || 0;
      return /tb/i.test(s) ? n * 1024 : n;
    };
    const sorted = Array.from(set).sort((a, b) => toGB(a) - toGB(b));
    return ["all", ...sorted];
  }, [categoryProducts]);

  const priceCeiling = useMemo(() => {
    const max = Math.max(50, ...categoryProducts.map((p) => p.lease_monthly || 0));
    return Math.ceil(max / 10) * 10;
  }, [categoryProducts]);

  // Year filter applies to iPads and MacBooks (hidden unless 2+ distinct years exist).
  const showYearFilter = category === "ipad" || category === "macbook";
  const years = useMemo(() => {
    if (!showYearFilter) return [];
    const set = new Set();
    categoryProducts.forEach((p) => { const y = getYear(p); if (y) set.add(y); });
    return ["all", ...Array.from(set).sort((a, b) => Number(b) - Number(a))];
  }, [categoryProducts, showYearFilter]);

  useEffect(() => { setMaxMonthly(priceCeiling); }, [priceCeiling]);

  const filtered = useMemo(() => {
    return categoryProducts.filter((p) => {
      const q = query.trim().toLowerCase();
      const matchesQuery = !q || `${p.name} ${p.model} ${p.color}`.toLowerCase().includes(q);
      const matchesStorage =
        storage === "all" ||
        (p.storage || "").split(/,\s*/).map((s) => s.trim()).includes(storage);
      const matchesPrice = (p.lease_monthly || 0) <= maxMonthly;
      const opt = (cfg.filterOptions || []).find((o) => o.key === carrier);
      const matchesCarrier = carrier === "all" || matchFilterOption(opt, p, cfg.filterKey);
      const matchesYear = !showYearFilter || year === "all" || getYear(p) === year;
      return matchesQuery && matchesStorage && matchesPrice && matchesCarrier && matchesYear;
    });
  }, [categoryProducts, query, storage, maxMonthly, carrier, year, showYearFilter]);

  const activeFilters = query || storage !== "all" || carrier !== "all" || year !== "all" || maxMonthly < priceCeiling;
  const clearAll = () => { setQuery(""); setStorage("all"); setCarrier("all"); setYear("all"); setMaxMonthly(priceCeiling); };

  const sorted = useMemo(() => {
    const arr = [...filtered];
    switch (sort) {
      case "price-asc": arr.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
      case "price-desc": arr.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
      case "name-asc": arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
      default: {
        // Featured — same as homepage: featured phones first in the admin drag order,
        // then everything else (stable: keeps newest-first for non-featured).
        const rank = (p) => (p.featured ? (p.featured_order ?? 1e9) : 1e18);
        arr.sort((a, b) => rank(a) - rank(b));
        break;
      }
    }
    return arr;
  }, [filtered, sort]);

  return (
    <div className="pt-32" data-testid={cfg.testid}>
      <section className="mx-auto max-w-7xl px-6 pb-12 lg:px-12">
        <Reveal>
          <h1 className="mt-3 font-display text-5xl font-black tracking-tighter text-ink sm:text-6xl">
            {cfg.heading}
          </h1>
          <p className="mt-5 max-w-xl text-lg text-muted-foreground">
            Every device is PhoneCheck certified 100% functional and backed by a 1-year replacement warranty. Lease-to-own or purchase outright.
          </p>
        </Reveal>
      </section>

      <TrustMarquee />

      <section className="mx-auto max-w-7xl px-6 py-12 lg:px-12">
        {/* Filter bar */}
        <div className="mb-10 rounded-3xl border border-line bg-white p-6" data-testid="filter-bar">
          <div className="mb-6">
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Search model</label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={cfg.placeholder}
                data-testid="filter-search"
                className="w-full rounded-xl border border-line py-3 pl-10 pr-4 outline-none transition-colors focus:border-crimson"
              />
            </div>
          </div>

          <div className="flex flex-col gap-5 lg:flex-row lg:flex-wrap lg:items-end lg:gap-8">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Storage</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {storages.map((s) => (
                <button
                  key={s}
                  onClick={() => setStorage(s)}
                  data-testid={`filter-storage-${s}`}
                  className={`rounded-full border px-4 py-2 text-sm font-bold transition-colors ${storage === s ? "border-crimson bg-crimson text-white" : "border-line bg-white text-ink hover:border-ink"}`}
                >
                  {s === "all" ? "All" : s}
                </button>
              ))}
            </div>
          </div>

          {cfg.filterOptions && (
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{cfg.filterLabel}</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {cfg.filterOptions.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCarrier(c.key)}
                  data-testid={`filter-${cfg.filterKey}-${c.key}`}
                  className={`rounded-full border px-4 py-2 text-sm font-bold transition-colors ${carrier === c.key ? "border-crimson bg-crimson text-white" : "border-line bg-white text-ink hover:border-ink"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          )}

          {showYearFilter && years.length > 1 && (
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Year</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {years.map((y) => (
                  <button
                    key={y}
                    onClick={() => setYear(y)}
                    data-testid={`filter-year-${y}`}
                    className={`rounded-full border px-4 py-2 text-sm font-bold transition-colors ${year === y ? "border-crimson bg-crimson text-white" : "border-line bg-white text-ink hover:border-ink"}`}
                  >
                    {y === "all" ? "All" : y}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="sort-select" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sort by</label>
            <select
              id="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="filter-sort"
              className="mt-2 block rounded-full border border-line bg-white px-4 py-2 text-sm font-bold text-ink outline-none transition-colors hover:border-ink focus:border-crimson"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name-asc">Name: A–Z</option>
            </select>
          </div>

          {activeFilters && (
            <button onClick={clearAll} data-testid="filter-clear" className="flex items-center gap-1 text-sm font-bold text-muted-foreground hover:text-crimson">
              <X className="h-4 w-4" /> Clear
            </button>
          )}
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-muted-foreground" data-testid="products-loading">Loading {cfg.noun}s…</div>
        ) : sorted.length === 0 ? (
          <div className="py-24 text-center" data-testid="products-empty">
            <p className="text-lg font-bold text-ink">No {cfg.noun}s match your filters.</p>
            <button onClick={clearAll} className="mt-4 rounded-full bg-ink px-6 py-2.5 text-sm font-bold text-white hover:bg-crimson">Reset filters</button>
          </div>
        ) : (
          <>
            <div className="mb-6 text-sm font-bold text-muted-foreground" data-testid="products-count">{sorted.length} device{sorted.length !== 1 ? "s" : ""}</div>
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-4" data-testid="products-grid">
              {sorted.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </>
        )}
      </section>

      <section className="bg-alt py-20">
        <div className="mx-auto flex max-w-4xl flex-col items-center px-6 text-center">
          <h2 className="font-display text-3xl font-black tracking-tighter text-ink sm:text-4xl">Don't see it? We can still help.</h2>
          <p className="mt-3 max-w-lg text-muted-foreground">Apply for the Payvantage leasing program and our team will match you with the right device.</p>
          <div className="mt-7"><ApplyButton testId="products-apply-btn" /></div>
        </div>
      </section>
    </div>
  );
}
